if (!customElements.get('featured-collection-with-tabs')) {
  class FeaturedCollectionWithTabs extends HTMLElement {
    constructor() {
      super();

      this.handleTabClick = this.handleTabClick.bind(this);
      this.handleTabKeydown = this.handleTabKeydown.bind(this);
      this.handleNavClick = this.handleNavClick.bind(this);
      this.handleSliderScroll = this.handleSliderScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      this.tabs = [...this.querySelectorAll('.featured-collections__tab-item[data-id]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"][data-id]')];
      this.tabList = this.querySelector('[role="tablist"]');
      this.nav = this.querySelector('.featured-collections__nav');
      this.prevBtn = this.nav?.querySelector('button[name="prev"]');
      this.nextBtn = this.nav?.querySelector('button[name="next"]');
      this.rtl = document.dir === 'rtl';

      this.tabList?.addEventListener('click', this.handleTabClick);
      this.tabList?.addEventListener('keydown', this.handleTabKeydown);
      this.nav?.addEventListener('click', this.handleNavClick);

      this.panels.forEach((panel) => {
        panel.querySelector('.slider')?.addEventListener('scroll', this.handleSliderScroll);
      });

      window.addEventListener('resize', this.handleResize);
      window.addEventListener('on:debounced-resize', this.handleResize);

      this.activateInitialPanel();
      customElements.whenDefined('carousel-slider').then(() => {
        requestAnimationFrame(() => {
          this.refreshActiveCarousel();
          this.updateNavState();
        });
      });
    }

    disconnectedCallback() {
      this.tabList?.removeEventListener('click', this.handleTabClick);
      this.tabList?.removeEventListener('keydown', this.handleTabKeydown);
      this.nav?.removeEventListener('click', this.handleNavClick);

      this.panels?.forEach((panel) => {
        panel.querySelector('.slider')?.removeEventListener('scroll', this.handleSliderScroll);
      });

      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('on:debounced-resize', this.handleResize);
    }

    activateInitialPanel() {
      const activeTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];

      if (activeTab) {
        this.activateTab(activeTab, false);
        return;
      }

      this.activePanel = this.panels.find((panel) => !panel.hidden) || this.panels[0];
      this.updateNavState();
    }

    handleTabClick(evt) {
      const tab = evt.target.closest('.featured-collections__tab-item[data-id]');
      if (!tab || tab.getAttribute('aria-selected') === 'true') return;

      this.activateTab(tab, true);
    }

    handleTabKeydown(evt) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(evt.key)) return;

      evt.preventDefault();

      const activeIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      let nextIndex = activeIndex > -1 ? activeIndex : 0;

      if (evt.key === 'Home') {
        nextIndex = 0;
      } else if (evt.key === 'End') {
        nextIndex = this.tabs.length - 1;
      } else if ((evt.key === 'ArrowRight' && !this.rtl) || (evt.key === 'ArrowLeft' && this.rtl)) {
        nextIndex = activeIndex === this.tabs.length - 1 ? 0 : activeIndex + 1;
      } else {
        nextIndex = activeIndex <= 0 ? this.tabs.length - 1 : activeIndex - 1;
      }

      this.activateTab(this.tabs[nextIndex], true);
      this.tabs[nextIndex].focus();
    }

    activateTab(tab, updateFocus) {
      const activeId = tab.dataset.id;

      this.tabs.forEach((item) => {
        const active = item === tab;
        item.classList.toggle('active', active);
        item.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active) {
          item.removeAttribute('tabindex');
        } else {
          item.setAttribute('tabindex', '-1');
        }
      });

      this.panels.forEach((panel) => {
        const active = panel.dataset.id === activeId;
        panel.hidden = !active;
        panel.classList.toggle('hidden', !active);
        if (active) this.activePanel = panel;
      });

      if (updateFocus) tab.focus();

      this.syncNavControls();
      requestAnimationFrame(() => {
        this.refreshActiveCarousel();
        this.updateNavState();
      });
    }

    syncNavControls() {
      const slider = this.getActiveSlider();
      if (!slider || !this.prevBtn || !this.nextBtn) return;

      this.prevBtn.setAttribute('aria-controls', slider.id);
      this.nextBtn.setAttribute('aria-controls', slider.id);
    }

    handleNavClick(evt) {
      const button = evt.target.closest('.slider-nav__btn');
      if (!button || button.disabled) return;

      const slider = this.getActiveSlider();
      if (!slider) return;

      const slideSpan = this.getSlideSpan();
      if (!slideSpan) return;

      const slidesToScroll = this.getSlidesToScroll(slideSpan);
      const distance = slidesToScroll * slideSpan;
      const forward = (button.name === 'next' && !this.rtl) || (button.name === 'prev' && this.rtl);
      const scrollPos = forward ? slider.scrollLeft + distance : slider.scrollLeft - distance;

      slider.scrollTo({ left: scrollPos, behavior: 'smooth' });
    }

    handleSliderScroll(evt) {
      if (evt.target !== this.getActiveSlider()) return;
      this.updateNavState();
    }

    handleResize() {
      requestAnimationFrame(() => {
        this.refreshActiveCarousel();
        this.updateNavState();
      });
    }

    refreshActiveCarousel() {
      const carousel = this.activePanel?.matches('carousel-slider') ? this.activePanel : null;

      if (carousel?.slider && carousel?.grid && typeof carousel.handleBreakpointChange === 'function') {
        carousel.handleBreakpointChange();
      }
    }

    updateNavState() {
      if (!this.prevBtn || !this.nextBtn) return;

      const slider = this.getActiveSlider();
      if (!slider) {
        this.setButtonDisabled(this.prevBtn, true);
        this.setButtonDisabled(this.nextBtn, true);
        return;
      }

      const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);
      const scrollLeft = Math.abs(slider.scrollLeft);
      const canScroll = maxScroll > 1;

      this.setButtonDisabled(this.prevBtn, !canScroll || scrollLeft <= 1);
      this.setButtonDisabled(this.nextBtn, !canScroll || scrollLeft >= maxScroll - 1);
    }

    setButtonDisabled(button, disabled) {
      button.disabled = disabled;
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }

    getActiveSlider() {
      return this.activePanel?.querySelector('.slider') || null;
    }

    getActiveSlides() {
      return [...(this.activePanel?.querySelectorAll('.slider__item:not([hidden])') || [])];
    }

    getSlideSpan() {
      const slides = this.getActiveSlides();
      if (slides.length < 2) return 0;

      return Math.abs(slides[1].getBoundingClientRect().left - slides[0].getBoundingClientRect().left);
    }

    getSlidesToScroll(slideSpan) {
      const slider = this.getActiveSlider();
      if (!slider || !slideSpan) return 1;

      const slideGap = slideSpan - this.getActiveSlides()[0].clientWidth;
      const slidesPerPage = Math.round((slider.clientWidth + slideGap) / slideSpan);

      return window.theme?.settings?.sliderItemsPerNav === 'page' ? slidesPerPage : 1;
    }
  }

  customElements.define('featured-collection-with-tabs', FeaturedCollectionWithTabs);
}
